'use client';

import { Button, Feature, Heading, LayoutContainer, Picture, TextPassage } from '@petsmart-ui/sparky';
import ReactPlayer from 'react-player';

import { useDeviceType } from '@libs/common-next';

import { trackFeatureBannerClick } from './analytics';
import type { FeatureBannerProps } from './types';
import './feature-banner.scss';

export type { FeatureBannerProps } from './types';

export function FeatureBanner({
    header,
    description,
    headingTagName,
    color,
    backgroundColor,
    media,
    cta,
}: Readonly<FeatureBannerProps>) {
    const { isMobile } = useDeviceType();

    const style = { backgroundColor, color };

    const displayHeader = header.trim();

    const bannerMedia =
        media.type === 'image' ? (
            <Picture items={media.items} imgSrc={media.src} imgAlt={media.alt} />
        ) : (
            <div className="feature-banner-video-container">
                <ReactPlayer
                    data-testid="video-player"
                    url={media.url}
                    onPlay={() => {
                        trackFeatureBannerClick('video', header);
                    }}
                    controls
                    width="100%"
                    height="100%"
                    playsinline
                    loop={media.loop}
                    playing={media.autoplay}
                    // muted required for autoplay
                    muted={media.autoplay}
                />
            </div>
        );

    return (
        <LayoutContainer className="feature-banner-container">
            <Feature className="feature-banner" media={bannerMedia} style={style}>
                {displayHeader ? (
                    <Heading tagName={headingTagName} size="title">
                        {displayHeader}
                    </Heading>
                ) : (
                    <span className="sparky-c-heading sparky-c-heading--title" />
                )}
                <TextPassage>{description}</TextPassage>
                {cta && (
                    <a
                        data-testid="feature-banner-cta"
                        target={cta.target}
                        href={cta.href}
                        onClick={() => {
                            if (cta.label) {
                                trackFeatureBannerClick(cta.label, header);
                            }
                        }}
                        rel="noreferrer"
                        className="feature-banner__link"
                    >
                        <Button variant={cta.variant} text={cta.label} fullWidth={isMobile} size="lg" />
                    </a>
                )}
            </Feature>
        </LayoutContainer>
    );
}
